import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { InputMask, type InputMaskProps } from "@/components/fields/mask-input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface FormCurrencyCentsProps
  extends Omit<InputMaskProps, "onChange" | "preset" | "value"> {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Currency input that displays formatted currency ($ 100,00)
 * but stores the value as cents (integer: 10000)
 */
export function FormCurrencyInputCents({
  name,
  label,
  placeholder = "$0,00",
  required,
  disabled,
  ...props
}: FormCurrencyCentsProps) {
  const form = useFormContext();

  // Convert cents to decimal for display
  const centsToDecimal = useCallback((cents: number | string | null | undefined): number => {
    if (cents === null || cents === undefined || cents === "") return 0;
    return Number(cents) / 100;
  }, []);

  // Convert decimal to cents for storage
  const decimalToCents = useCallback((decimal: number | string): number => {
    if (decimal === "" || decimal === null || decimal === undefined) return 0;
    return Math.round(Number(decimal) * 100);
  }, []);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && <FormLabel required={required}>{label}</FormLabel>}
          <FormControl>
            <InputMask
              {...props}
              preset="currency"
              placeholder={placeholder}
              value={centsToDecimal(field.value)}
              onChange={(rawValue) => {
                const cents = decimalToCents(rawValue);
                field.onChange(cents);
              }}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
